import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Award, Smartphone, Zap } from 'lucide-react';

const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Mais Clientes',
      description: 'Aumente sua visibilidade e converta visitantes em clientes reais',
      highlight: 'Maior',
      highlightText: 'conversão garantida',
      color: 'blue',
    },
    {
      icon: Award,
      title: 'Mais Autoridade',
      description: 'Transmita profissionalismo e destaque-se da concorrência',
      highlight: 'Total',
      highlightText: 'credibilidade',
      color: 'purple',
    },
    {
      icon: Zap,
      title: 'Alta Conversão',
      description: 'Cada elemento estrategicamente posicionado para gerar ação',
      highlight: 'Ultra',
      highlightText: 'rápido',
      color: 'green',
    },
    {
      icon: Smartphone,
      title: 'Mobile-First',
      description: 'Funciona perfeitamente em qualquer dispositivo',
      highlight: 'Total',
      highlightText: 'responsividade',
      color: 'orange',
    },
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-600',
      text: 'text-blue-600',
      light: 'bg-blue-50',
      border: 'border-blue-200',
    },
    purple: {
      bg: 'bg-purple-600',
      text: 'text-purple-600',
      light: 'bg-purple-50',
      border: 'border-purple-200',
    },
    green: {
      bg: 'bg-green-600',
      text: 'text-green-600',
      light: 'bg-green-50',
      border: 'border-green-200',
    },
    orange: {
      bg: 'bg-orange-600',
      text: 'text-orange-600',
      light: 'bg-orange-50',
      border: 'border-orange-200',
    },
  };

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6 lg:px-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            O que você <span className="text-blue-600">ganha</span>
          </h2>
          <p className="text-xl text-gray-600">
            Benefícios reais que transformam seu negócio
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.05 }}
              className={`${colorClasses[benefit.color].light} border-2 ${colorClasses[benefit.color].border} p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group`}
            >
              {/* Background Effect */}
              <div className={`absolute top-0 right-0 w-32 h-32 ${colorClasses[benefit.color].bg} opacity-5 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500`}></div>
              
              <div className="relative z-10">
                {/* Icon */}
                <div className={`w-16 h-16 ${colorClasses[benefit.color].bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <benefit.icon className="text-white" size={32} />
                </div>

                {/* Highlight Text */}
                <div className="mb-4">
                  <p className={`text-3xl font-bold ${colorClasses[benefit.color].text}`}>
                    {benefit.highlight}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">
                    {benefit.highlightText}
                  </p>
                </div>

                {/* Title and Description */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-6xl font-bold mb-2">24/7</p>
              <p className="text-blue-100 text-lg">Seu negócio trabalhando sempre</p>
            </div>
            <div>
              <p className="text-6xl font-bold mb-2">100%</p>
              <p className="text-blue-100 text-lg">Mobile responsivo</p>
            </div>
            <div>
              <p className="text-6xl font-bold mb-2">&lt;3s</p>
              <p className="text-blue-100 text-lg">Tempo de carregamento</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;
